/**
 * Lifecycle actions: Won / Lost / Listed / Sold transitions and the
 * outcome modal that records the economics (hammer price, fees, sale price).
 */
import { applyLifecycleUpdate, type LifecycleDeps } from '../../modules/lifecycle';
import { getSellChannels } from '../../modules/sellChannels';
import { applyRemoteSnapshot, mapRemoteDocument } from '../../modules/lotOperations';
import { parseCurrencyValue } from '../../modules/formatting';
import type { Lot } from '../../types';
import type { TableHandlerDeps } from './types';

function lifecycleDeps(deps: TableHandlerDeps): LifecycleDeps {
  return {
    state: deps.state,
    render: deps.render,
    ensureAccessToken: deps.ensureAccessToken,
    mapRemoteDocument,
    applyRemoteSnapshot,
  };
}

function findLot(deps: TableHandlerDeps, id: string): Lot | undefined {
  return deps.state.lots.find(lot => lot && lot.id === id);
}

// ── Outcome modal ──────────────────────────────────────────────────────

interface OutcomeModalElements {
  modal: HTMLElement;
  backdrop: HTMLElement;
  title: HTMLElement | null;
  wonFields: HTMLElement | null;
  soldFields: HTMLElement | null;
  hammerInput: HTMLInputElement | null;
  feesInput: HTMLInputElement | null;
  salePriceInput: HTMLInputElement | null;
  channelSelect: HTMLSelectElement | null;
  soldDateInput: HTMLInputElement | null;
}

function getOutcomeModalElements(): OutcomeModalElements | null {
  const modal = document.getElementById('outcome-modal');
  const backdrop = document.getElementById('outcome-modal-backdrop');
  if (!modal || !backdrop) return null;
  return {
    modal,
    backdrop,
    title: document.getElementById('outcome-modal-title'),
    wonFields: document.getElementById('outcome-won-fields'),
    soldFields: document.getElementById('outcome-sold-fields'),
    hammerInput: document.getElementById('outcome-hammer-price') as HTMLInputElement | null,
    feesInput: document.getElementById('outcome-fees-paid') as HTMLInputElement | null,
    salePriceInput: document.getElementById('outcome-sale-price') as HTMLInputElement | null,
    channelSelect: document.getElementById('outcome-sale-channel') as HTMLSelectElement | null,
    soldDateInput: document.getElementById('outcome-sold-date') as HTMLInputElement | null,
  };
}

function closeOutcomeModal(deps: TableHandlerDeps): void {
  const els = getOutcomeModalElements();
  if (!els) return;
  els.modal.setAttribute('hidden', '');
  els.backdrop.setAttribute('hidden', '');
  document.body?.classList.remove('modal-open');
  deps.state.outcomeEdit = null;
}

function openOutcomeModal(deps: TableHandlerDeps, lot: Lot, mode: 'won' | 'sold'): void {
  const els = getOutcomeModalElements();
  if (!els) return;

  deps.state.outcomeEdit = { id: lot.id, mode };

  if (els.title) {
    els.title.textContent = mode === 'won' ? 'Record Win' : 'Record Sale';
  }
  if (els.wonFields) els.wonFields.hidden = mode !== 'won';
  if (els.soldFields) els.soldFields.hidden = mode !== 'sold';

  if (mode === 'won') {
    // Prefill the hammer with the user's bid, else the last known bid.
    const hammer =
      typeof lot.bidAmount === 'number' && Number.isFinite(lot.bidAmount)
        ? lot.bidAmount
        : (parseCurrencyValue(lot.currentBid ?? lot.currentBidRaw) ?? null);
    if (els.hammerInput) {
      els.hammerInput.value =
        lot.outcome?.hammerPrice != null
          ? String(lot.outcome.hammerPrice)
          : hammer != null
            ? hammer.toFixed(2)
            : '';
    }
    if (els.feesInput) {
      els.feesInput.value = lot.outcome?.feesPaid != null ? String(lot.outcome.feesPaid) : '';
    }
  } else {
    if (els.salePriceInput) {
      els.salePriceInput.value =
        lot.outcome?.salePrice != null ? String(lot.outcome.salePrice) : '';
    }
    if (els.channelSelect) {
      // (Re)populate channels, keeping the "no fees" default first.
      while (els.channelSelect.options.length > 1) els.channelSelect.remove(1);
      for (const channel of getSellChannels(deps.state.settings)) {
        const option = document.createElement('option');
        option.value = channel.id;
        option.textContent = `${channel.label} (${channel.sellerCostsPct}%)`;
        els.channelSelect.add(option);
      }
      els.channelSelect.value = lot.outcome?.saleChannel ?? lot.sellChannel ?? '';
      if (els.channelSelect.value !== (lot.outcome?.saleChannel ?? lot.sellChannel ?? '')) {
        els.channelSelect.value = '';
      }
    }
    if (els.soldDateInput) {
      const existing = lot.outcome?.soldAt ? lot.outcome.soldAt.slice(0, 10) : '';
      els.soldDateInput.value = existing || new Date().toISOString().slice(0, 10);
    }
  }

  els.modal.removeAttribute('hidden');
  els.backdrop.removeAttribute('hidden');
  document.body?.classList.add('modal-open');
  window.requestAnimationFrame(() => {
    const focusTarget = mode === 'won' ? els.hammerInput : els.salePriceInput;
    focusTarget?.focus();
    focusTarget?.select();
  });
}

function submitOutcomeModal(deps: TableHandlerDeps): void {
  const edit = deps.state.outcomeEdit;
  const els = getOutcomeModalElements();
  if (!edit || !els) return;

  const parseMoney = (input: HTMLInputElement | null): number | null => {
    const raw = input?.value?.trim() ?? '';
    if (!raw) return null;
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) / 100 : null;
  };

  const nowIso = new Date().toISOString();

  if (edit.mode === 'won') {
    const hammerPrice = parseMoney(els.hammerInput);
    if (hammerPrice == null) {
      els.hammerInput?.focus();
      return;
    }
    const feesPaid = parseMoney(els.feesInput);
    void applyLifecycleUpdate(
      edit.id,
      'won',
      { hammerPrice, feesPaid, decidedAt: nowIso },
      lifecycleDeps(deps)
    );
  } else {
    const salePrice = parseMoney(els.salePriceInput);
    if (salePrice == null) {
      els.salePriceInput?.focus();
      return;
    }
    const channel = els.channelSelect?.value?.trim() || undefined;
    const soldDate = els.soldDateInput?.value?.trim();
    const soldAt =
      soldDate && /^\d{4}-\d{2}-\d{2}$/.test(soldDate)
        ? new Date(soldDate + 'T12:00:00Z').toISOString()
        : nowIso;
    void applyLifecycleUpdate(
      edit.id,
      'sold',
      { salePrice, saleChannel: channel, soldAt },
      lifecycleDeps(deps)
    );
  }

  closeOutcomeModal(deps);
}

/** Wire the outcome modal form/cancel/backdrop once at boot. */
export function setupOutcomeModalHandlers(deps: TableHandlerDeps): void {
  const form = document.getElementById('outcome-form');
  const cancel = document.getElementById('outcome-cancel');
  const close = document.getElementById('close-outcome-modal');
  const backdrop = document.getElementById('outcome-modal-backdrop');

  form?.addEventListener('submit', e => {
    e.preventDefault();
    submitOutcomeModal(deps);
  });
  cancel?.addEventListener('click', () => closeOutcomeModal(deps));
  close?.addEventListener('click', () => closeOutcomeModal(deps));
  backdrop?.addEventListener('click', () => closeOutcomeModal(deps));
}

// ── Row actions ────────────────────────────────────────────────────────

export function handleMarkWon(id: string, e: Event, deps: TableHandlerDeps): void {
  e.preventDefault();
  const lot = findLot(deps, id);
  if (lot) openOutcomeModal(deps, lot, 'won');
}

export function handleMarkLost(id: string, e: Event, deps: TableHandlerDeps): void {
  e.preventDefault();
  if (!findLot(deps, id)) return;
  void applyLifecycleUpdate(
    id,
    'lost',
    { decidedAt: new Date().toISOString() },
    lifecycleDeps(deps)
  );
}

export function handleMarkListed(id: string, e: Event, deps: TableHandlerDeps): void {
  e.preventDefault();
  if (!findLot(deps, id)) return;
  void applyLifecycleUpdate(id, 'listed', undefined, lifecycleDeps(deps));
}

export function handleMarkSold(id: string, e: Event, deps: TableHandlerDeps): void {
  e.preventDefault();
  const lot = findLot(deps, id);
  if (lot) openOutcomeModal(deps, lot, 'sold');
}
